import React, { useMemo, useEffect, useState, useRef, useLayoutEffect, useCallback } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { getDepartments } from "../store/actions/departmentActions";
import { setActiveDepartment } from "../store/actions/departmentActions";
import { getBowtie, createBowtie, saveBowtie, editControl, gotoRiskDashboard } from "../store/actions/riskActions";
import TopBar from "../components/TopBar";
import Sidebar from "../components/Sidebar";
import UnitsSideBar from "../components/UnitsSideBar";
import UnitsTopBar from "../components/UnitsTopBar";
import DraggableControlPanel from "../components/Risk/DraggableControlPanel.js";
import DraggableThreatCard from "../components/Risk/DraggableThreatCard.js";
import DraggableConsequenceCard from "../components/Risk/DraggableConsequenceCard.js";
import { RiFlowChart, RiAddLine, RiAlertLine, RiErrorWarningLine } from "react-icons/ri"
import { useHistory } from "react-router-dom";
import { FiTrash2, FiShield, FiPlus } from "react-icons/fi";
import { LuZoomIn, LuZoomOut, LuRotateCcw } from "react-icons/lu";
import ControlOverlay from "../components/Control/ControlOverlay";
import Popup from 'reactjs-popup';
import { useDispatch, useSelector } from "react-redux";
import { LuActivity } from "react-icons/lu"; 
import { LuLogOut } from "react-icons/lu";



export function useDragWindowLike(initial = { x: 0, y: 0 }) {
    const [pos, setPos] = useState(initial);
    const dragRef = useRef(null); // { dx, dy }

    useEffect(() => {
        function onMove(e) {
            if (!dragRef.current) return;
            setPos({ x: e.clientX - dragRef.current.dx, y: e.clientY - dragRef.current.dy });
        }
        function onUp() {
            dragRef.current = null;
            document.body.style.userSelect = "";
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        }
        // cleanup when unmount
        return () => onUp();
    }, []);

    function beginDrag(e) {
        const parent = e.currentTarget.parentElement; // the card container
        const rect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
        const dx = e.clientX - rect.left;
        const dy = e.clientY - rect.top;
        dragRef.current = { dx, dy };
        document.body.style.userSelect = "none";

        function onMove(ev) {
            if (!dragRef.current) return;
            setPos({ x: ev.clientX - dragRef.current.dx, y: ev.clientY - dragRef.current.dy });
        }
        function onUp() {
            dragRef.current = null;
            document.body.style.userSelect = "";
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        }

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp, { once: true });
    }

    return { pos, setPos, beginDrag };
}


export function toClientIdDb(id) {
    return `db-${id}`; // keep your current convention; or use `t-${id}` / `c-${id}` if you want
}

export function normalizeBowtieToForm(api) {
    // helper: coerce to 0–100 or null
    const toPct = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return null;
        return Math.max(0, Math.min(100, n));
    };

    // threats (keep DB id so hydrators can map!)
    const BowtieThreats = (api.threats ?? []).map((t) => ({
        ClientID: toClientIdDb(t.ThreatID),
        ThreatID: t.ThreatID ?? null,
        Text: t.Text ?? "",
        OrderIndex: t.OrderIndex ?? 0,
    }));

    // threat controls grouped by owner client id
    const ThreatCtrlByOwner = {};
    (api.threatControls ?? []).forEach((tc) => {
        const ownerCID = toClientIdDb(tc.ThreatID);
        (ThreatCtrlByOwner[ownerCID] ||= []).push({
            ClientID: toClientIdDb(tc.ThreatControlID),
            OwnerClientID: ownerCID,
            Text: tc.Text ?? "",
            OrderIndex: tc.OrderIndex ?? 0,

            // ids for pre/post save
            ThreatControlID: tc.ThreatControlID ?? null, // link-row PK
            DbControlID: tc.control_id ?? null,          // FK → 3457_Controls

            // type & efficiency
            Type: "Preventive",
            efficiency: toPct(tc.EfficiencyLevel ?? tc.efficiency_level), // ← NEW
        });
    });
    Object.values(ThreatCtrlByOwner).forEach((arr) =>
        arr.sort((a, b) => (a.OrderIndex ?? 0) - (b.OrderIndex ?? 0))
    );

    // consequences (keep DB id)
    const BowtieConsequences = (api.consequences ?? []).map((c) => ({
        ClientID: toClientIdDb(c.ConsequenceID),
        ConsequenceID: c.ConsequenceID ?? null,
        Text: c.Text ?? "",
        OrderIndex: c.OrderIndex ?? 0,
    }));

    // consequence controls
    const ConsequenceCtrlByOwner = {};
    (api.consequenceControls ?? []).forEach((cc) => {
        const ownerCID = toClientIdDb(cc.ConsequenceID);
        (ConsequenceCtrlByOwner[ownerCID] ||= []).push({
            ClientID: toClientIdDb(cc.ConsequenceControlID),
            OwnerClientID: ownerCID,
            Text: cc.Text ?? "",
            OrderIndex: cc.OrderIndex ?? 0,

            ConsequenceControlID: cc.ConsequenceControlID ?? null, // link-row PK
            DbControlID: cc.control_id ?? null,                    // FK → 3457_Controls

            Type: "Mitigative",
            efficiency: toPct(cc.EfficiencyLevel ?? cc.efficiency_level), // ← NEW
        });
    });
    Object.values(ConsequenceCtrlByOwner).forEach((arr) =>
        arr.sort((a, b) => (a.OrderIndex ?? 0) - (b.OrderIndex ?? 0))
    );

    return {
        BowtieThreats,
        ThreatCtrlByOwner,
        BowtieConsequences,
        ConsequenceCtrlByOwner,
    };
}

const BowtieAnalysis = (props) => {
    const { go_to_risk_dashboard, editControlFN, saveBowtieFN, match, departments, getDepartmentsFN, activeDepartmentId, setActiveDepartmentId, getBowtieFN, createBowtieFN, bowtie } = props;
    const risk_id = match.params.id;

    useEffect(() => {
        getDepartmentsFN();
    }, [getDepartmentsFN]);

    const dispatch = useDispatch();

    // Sample Data

    const [formState, setFormState] = useState({
        BowtieConsequences: [],
        BowtieThreats: [],
        ThreatCtrlByOwner: {},       // { [Threat.ClientID]: Control[] }
        ConsequenceCtrlByOwner: {},  // { [Cons.ClientID]: Control[] }
    });

    const [openPanel, setOpenPanel] = useState(false);
    const [panelPos, setPanelPos] = useState({ x: 24, y: 24 }); // starting corner

    const draggingThreatRef = useRef({ id: null, pt: null });
    const draggingConsRef = useRef({ id: null, pt: null });

    const canvasRef = useRef(null);
    const threatCtrlsHydrated = React.useRef(false);
    const consequenceCtrlsHydrated = React.useRef(false);
    const hazardRef = useRef(null);
    const threatRefs = useRef({});
    const [hoveredThreat, setHoveredThreat] = useState(null);
    const [hoveredConsequence, setHoveredConsequence] = useState(null);
    //const [threatLines, setThreatLines] = useState([]);
    const [showActions, setShowActions] = useState(false);

    const [selectedControl, setSelectedControl] = useState(null);

    const consequenceRefs = useRef({});               // Consequence ClientID -> element
    const [center, setCenter] = useState({ x: 0, y: 0 });

    const [threatAnchors, setThreatAnchors] = useState({});

    const [consequenceAnchors, setConsequenceAnchors] = useState({});



    const worldSize = useRef({ w: 3000, h: 2000 }); // or whatever world size you want
    const [svgW, setSvgW] = useState(worldSize.current.w);
    const [svgH, setSvgH] = useState(worldSize.current.h);



    const ghostRef = { el: null };

    function beginPaletteDrag(e, type) {
        e.dataTransfer.effectAllowed = "copy";

        // tiny custom drag image
        const ghost = document.createElement("div");
        ghost.style.width = "24px";
        ghost.style.height = "24px";
        ghost.style.border = "2px solid #111827";
        ghost.style.borderRadius = "6px";
        ghost.style.background = "#fff";
        ghost.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
        ghost.style.display = "flex";
        ghost.style.alignItems = "center";
        ghost.style.justifyContent = "center";
        ghost.style.fontSize = "14px";
        ghost.style.fontWeight = "700";
        ghost.style.color = "#111827";
        ghost.textContent = type === "threat" ? "T" : "C";
        document.body.appendChild(ghost);
        ghostRef.el = ghost;
        e.dataTransfer.setDragImage(ghost, 12, 12);

        e.dataTransfer.setData("application/x-bowtie", JSON.stringify({ type }));
    }

    function endPaletteDrag() {
        if (ghostRef.el) {
            ghostRef.el.remove();
            ghostRef.el = null;
        }
    }


    useLayoutEffect(() => {
        if (!scaledLayerRef.current) return;
        const el = scaledLayerRef.current;
        // measure unscaled world size
        const w = el.scrollWidth || el.offsetWidth || 3000;
        const h = el.scrollHeight || el.offsetHeight || 2000;
        worldSize.current = { w, h };
        setSvgW(w);
        setSvgH(h);

        // keep in sync on resize
        const ro = new ResizeObserver(() => {
            const w2 = el.scrollWidth || el.offsetWidth || w;
            const h2 = el.scrollHeight || el.offsetHeight || h;
            if (w2 !== svgW) setSvgW(w2);
            if (h2 !== svgH) setSvgH(h2);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);


    const consAnchorsRef = useRef({});
    const consRaf = useRef(0);
    function reportConsequenceAnchor(id, pt) {
        consAnchorsRef.current[id] = pt;
        if (draggingConsRef.current.id === id) draggingConsRef.current.pt = pt;

        if (!consRaf.current) {
            consRaf.current = requestAnimationFrame(() => {
                consRaf.current = 0;
                setConsequenceAnchors({ ...consAnchorsRef.current });
            });
        }
    }

    const consequenceLines = useMemo(() => {
        const c = center;
        return (formState.BowtieConsequences || []).map(x => {
            const live = draggingConsRef.current.id === x.ClientID ? draggingConsRef.current.pt : null;
            const a = live || consequenceAnchors[x.ClientID];
            if (!a) return null;
            return { id: x.ClientID, ownerId: x.ClientID, x1: a.x, y1: a.y, x2: c.x, y2: c.y };
        }).filter(Boolean);
    }, [formState.BowtieConsequences, consequenceAnchors, center]);

    const makeCurveBi = ({ x1, y1, x2, y2 }) => {
        const dx = Math.max(40, Math.abs(x2 - x1) * 0.5);
        const dir = x1 <= x2 ? 1 : -1; // if start is right of end, flip handles
        const c1x = x1 + dir * dx, c1y = y1;
        const c2x = x2 - dir * dx, c2y = y2;
        return `M ${x1},${y1} C ${c1x},${c1y} ${c2x},${c2y} ${x2},${y2}`;
    };

    function makeHybridConnector({ x1, y1, x2, y2 }, bend = 90, curve = 60) {
        // bend point is 'bend' px away from the hazard along the line
        const vx = x2 - x1, vy = y2 - y1;
        const len = Math.hypot(vx, vy) || 1;

        const bx = x2 - (vx / len) * bend;
        const by = y2 - (vy / len) * bend;

        // slight perpendicular offset so the last segment turns nicely
        const nx = -vy / len, ny = vx / len; // unit normal
        const qcx = bx + nx * curve * 0.4;
        const qcy = by + ny * curve * 0.4;

        // M — straight line — quadratic bend into hazard
        return `M ${x1},${y1} L ${bx},${by} Q ${qcx},${qcy} ${x2},${y2}`;
    }

    function makeHorizBendConnector({ x1, y1, x2, y2 }, bend = 100, curve = 80) {
        const dir = x2 >= x1 ? 1 : -1;

        // where the straight horizontal segment ends (near the hazard in X, same Y as start)
        let xb = x2 - dir * bend;

        // don't let the bend start behind/overlap the start; keep at least 16px horizontal run
        if (dir > 0) xb = Math.max(x1 + 16, xb);
        else xb = Math.min(x1 - 16, xb);

        // vector from bend start → hazard (for a nice perpendicular control point)
        const vx = x2 - xb;
        const vy = y2 - y1;
        const len = Math.hypot(vx, vy) || 1;

        // unit normal to that vector
        const nx = -vy / len;
        const ny = vx / len;

        // quadratic bezier control point a bit off the corner
        const qcx = xb + nx * (curve * 0.6);
        const qcy = y1 + ny * (curve * 0.6);

        return `M ${x1},${y1} L ${xb},${y1} Q ${qcx},${qcy} ${x2},${y2}`;
    }


    const touchedConsRef = useRef(new Set());
    const touchedThreatRef = useRef(new Set());

    const cardKey = (id, startX, startY, touchedRef) =>
        touchedRef.current.has(id) ? id : `${id}:${startX},${startY}`;

    function makeHorizSmootherConnector(
        { x1, y1, x2, y2 },
        bend = 90,      // where the turn starts (px from hazard)
        tension = 0.65  // 0..1, higher = longer, smoother curve
    ) {
        const dir = x2 >= x1 ? 1 : -1;

        // end of horizontal segment
        let xb = x2 - dir * bend;
        xb = dir > 0 ? Math.max(x1 + 16, xb) : Math.min(x1 - 16, xb);

        // curve extent along X
        const spanX = Math.abs(x2 - xb);
        const curveX = Math.max(48, spanX * tension);

        // ✔ start tangent horizontal: c1y === y1
        const c1x = xb + dir * curveX * 0.35;
        const c1y = y1;

        // soft landing near hazard
        const c2x = x2 - dir * curveX * 0.65;
        const c2y = y2 - (y2 - y1) * 0.12; // small easing, not too steep

        return `M ${x1},${y1} L ${xb},${y1} C ${c1x},${c1y} ${c2x},${c2y} ${x2},${y2}`;
    }

    function getControlAnchors({ x1, y1, x2, y2 }, count, bend = 90, padding = 16) {
        const vx = x2 - x1, vy = y2 - y1;
        const len = Math.hypot(vx, vy) || 1;

        // bend point
        const bx = x2 - (vx / len) * bend;
        const by = y2 - (vy / len) * bend;

        // unit vector along straight segment
        const sx = bx - x1, sy = by - y1;
        const slen = Math.hypot(sx, sy) || 1;
        const ux = sx / slen, uy = sy / slen;

        // keep a bit of space from both ends
        const usable = Math.max(0, slen - padding * 2);
        const anchors = [];

        for (let i = 0; i < count; i++) {
            const t = (i + 0.5) / count; // centers
            const dist = padding + t * usable;
            anchors.push({
                x: x1 + ux * dist,
                y: y1 + uy * dist,
            });
        }
        return anchors;
    }

    function getHorizAnchors({ x1, y1, x2, y2 }, count, bend = 110, opts = {}) {
        const padding = opts.padding ?? 24;   // was 16
        const minGap = opts.minGap ?? 56;   // NEW: enforce at least 56px between chips
        if (!count) return [];

        const dir = x2 >= x1 ? 1 : -1;
        let xb = x2 - dir * bend;
        xb = dir > 0 ? Math.max(x1 + 16, xb) : Math.min(x1 - 16, xb);

        const segLen = Math.abs(xb - x1);
        const usable = Math.max(0, segLen - padding * 2);

        const gap = Math.max(minGap, usable / count);   // spread or enforce min spacing
        const anchors = [];
        for (let i = 0; i < count; i++) {
            const dist = padding + gap * (i + 0.5);       // center each chip in its slot
            if (dist > usable + padding) break;           // don't overflow when minGap wins
            anchors.push({ x: x1 + dir * dist, y: y1 });
        }
        return anchors;
    }


    function getHorizAnchorsFixedGap(
        { x1, y1, x2, y2 },
        count,
        { bend = 110, startPad = 8, gap = 170, firstFactor = 0.35, endPad = 4, fanStep = 8 } = {}
    ) {
        if (!count) return [];

        const dir = x2 >= x1 ? 1 : -1;

        // end of straight segment
        let xb = x2 - dir * bend;
        xb = dir > 0 ? Math.max(x1 + 16, xb) : Math.min(x1 - 16, xb);

        const segLen = Math.abs(xb - x1);
        const usable = Math.max(0, segLen - (startPad + endPad));
        const maxDist = startPad + usable;

        const anchors = [];
        let overIdx = 0;

        for (let i = 0; i < count; i++) {
            // first chip uses a smaller fraction of gap; the rest use full gap
            const distFromStart = i === 0
                ? firstFactor * gap
                : firstFactor * gap + i * gap;

            let dist = startPad + distFromStart;

            if (dist > maxDist) {
                dist = Math.max(startPad, maxDist - overIdx * fanStep);
                overIdx++;
            }
            anchors.push({ x: x1 + dir * dist, y: y1 });
        }
        return anchors;
    }

    function getHorizAnchorsFixedGapSide(
        { x1, y1, x2, y2 },
        count,
        {
            bend = 110,
            startPad = 32,   // padding near the start (hazard side)
            endPad = 32,     // padding near the end (card/bend side)
            gap = 170,       // spacing between controls after the first
            firstFactor = 0.5, // fraction of gap for the first step
            fanStep = 8,     // slight staggering when clamping
            side = "start",  // "start" for threats, "end" for consequences
        } = {}
    ) {
        if (!count) return [];

        const dir = x2 >= x1 ? 1 : -1;

        // end of the straight run (before the curve)
        let xb = x2 - dir * bend;
        xb = dir > 0 ? Math.max(x1 + 16, xb) : Math.min(x1 - 16, xb);

        const segLen = Math.abs(xb - x1);
        const usable = Math.max(0, segLen - (startPad + endPad));
        const maxFromStart = startPad + usable;   // measured from start
        const maxFromEnd = endPad + usable;     // measured from end

        const anchors = [];
        let overIdx = 0;

        for (let i = 0; i < count; i++) {
            if (side === "start") {
                // measure from start (hazard) — THREATS
                let dist = startPad + (firstFactor * gap) + i * gap;
                if (dist > maxFromStart) {
                    dist = Math.max(startPad, maxFromStart - overIdx * fanStep);
                    overIdx++;
                }
                anchors.push({ x: x1 + dir * dist, y: y1 });
            } else {
                // measure from END (near card) — CONSEQUENCES
                // distance from end towards start
                let back = endPad + (firstFactor * gap) + i * gap;
                if (back > maxFromEnd) {
                    back = Math.max(endPad, maxFromEnd - overIdx * fanStep);
                    overIdx++;
                }
                // position = end minus "back" along the straight axis
                const x = xb - dir * (back - endPad);
                anchors.push({ x, y: y1 });
            }
        }

        return anchors;
    }



    const CARD_W = 340;
    const H_GAP = 140;      // base horizontal gap from hazard


    function getThreatStartX(ownerId, centerX, formState) {

        const n = (formState.ThreatCtrlByOwner?.[ownerId] || []).length;
        const extra = Math.min(n, MAX_CTRL_BONUS) * PER_CTRL;
        // Card's LEFT should be CARD_W + gap to the left of center
        return centerX - (CARD_W + H_GAP + extra);
    }

    function getConsequenceStartX(ownerId, centerX, formState) {
        const n = (formState.ConsequenceCtrlByOwner?.[ownerId] || []).length;
        const extra = Math.min(n, MAX_CTRL_BONUS) * PER_CTRL;
        // Right side: card's LEFT is to the right of center by gap
        return centerX + (H_GAP + extra);
    }


    const threatAnchorsRef = useRef({});
    const threatRaf = useRef(0);
    function reportThreatAnchor(id, pt) {
        threatAnchorsRef.current[id] = pt;
        // if this one is currently dragging, also set the live override
        if (draggingThreatRef.current.id === id) draggingThreatRef.current.pt = pt;

        if (!threatRaf.current) {
            threatRaf.current = requestAnimationFrame(() => {
                threatRaf.current = 0;
                setThreatAnchors({ ...threatAnchorsRef.current });
            });
        }
    }

    function getRightStartC(canvasRef) {
        const w = canvasRef.current?.clientWidth ?? 1200;
        return Math.max(0, w - 24 - 340);
    }


    // get middle node anchor
    function getCenterPoint() {
        const cvsRect = canvasRef.current?.getBoundingClientRect();
        if (!cvsRect) return { x: 0, y: 0 };

        if (hazardRef.current) {
            const cRect = hazardRef.current.getBoundingClientRect();
            return {
                x: cRect.left + cRect.width / 2 - cvsRect.left,
                y: cRect.top + cRect.height / 2 - cvsRect.top,
            };
        }
        // fallback: geometric center of canvas
        return { x: cvsRect.width / 2, y: cvsRect.height / 2 };
    }

    // recompute on resize (center can move)
    useEffect(() => {
        function recalc() { setCenter(getCenterPoint()); }
        recalc();
        window.addEventListener("resize", recalc);
        return () => window.removeEventListener("resize", recalc);
    }, []);

    // build lines
    const threatLines = useMemo(() => {
        const c = center;
        return (formState.BowtieThreats || []).map(t => {
            const live = draggingThreatRef.current.id === t.ClientID ? draggingThreatRef.current.pt : null;
            const a = live || threatAnchors[t.ClientID];
            if (!a) return null;
            return { ownerId: t.ClientID, id: t.ClientID, x1: a.x, y1: a.y, x2: c.x, y2: c.y };
        }).filter(Boolean);
    }, [formState.BowtieThreats, threatAnchors, center]);

    function makeCurve(ln) {
        const { x1, y1, x2, y2 } = ln;
        const dx = Math.max(40, Math.abs(x2 - x1) * 0.5);
        const c1x = x1 + dx, c1y = y1;
        const c2x = x2 - dx, c2y = y2;
        return `M ${x1},${y1} C ${c1x},${c1y} ${c2x},${c2y} ${x2},${y2}`;
    }

    // (Optional) keep center in sync on scroll (if canvas scrolls)
    useEffect(() => {
        const el = canvasRef.current;
        if (!el) return;
        function onScroll() { setCenter(getCenterPoint()); }
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => el.removeEventListener("scroll", onScroll);
    }, []);






    const closeCreation = async (data, control_id, risk_id) => {
        console.log("Data :", data, "ID ", control_id, "RISK", risk_id)

        setSelectedControl(null)
        await updateServerData(data, control_id, risk_id);

    }

    const updateServerData = async (data, control_id, risk_id) => {


        const updatedData = { ...data };

        // Add one day to StartDate and EndDate if they exist
        if (updatedData.StartDate) {
            const startDate = new Date(updatedData.StartDate);
            startDate.setDate(startDate.getDate() + 1);
            updatedData.StartDate = startDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        }

        if (updatedData.EndDate) {
            const endDate = new Date(updatedData.EndDate);
            endDate.setDate(endDate.getDate() + 1);
            updatedData.EndDate = endDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        }

        //console.log("CONTROL DATA AFTER ADJUSTMENT", updatedData);

        await editControlFN(risk_id, control_id, updatedData);


    }

    const scaledLayerRef = useRef(null);

    const viewportRef = useRef(null);   // the visible area
    const panLayerRef = useRef(null);   // gets translated (panned)

    const [pan, setPan] = useState({ x: 0, y: 0 });
    const panRef = useRef({ x: 0, y: 0 });
    useEffect(() => { panRef.current = pan; }, [pan]);


    function getRect() {
        const el = canvasRef.current;
        if (!el) return { left: 0, top: 0, width: 0, height: 0 };
        return el.getBoundingClientRect();
    }

    // single-frame pan+zoom update (no flicker)
    function applyZoomAt(targetZ, anchorPx) {
        const rect = getRect();
        const oldZ = zoomRef.current;
        const zNew = clamp(+targetZ.toFixed(3));

        const vx = anchorPx?.x ?? rect.width / 2;
        const vy = anchorPx?.y ?? rect.height / 2;

        const worldX = (vx - panRef.current.x) / oldZ;
        const worldY = (vy - panRef.current.y) / oldZ;

        const newPanX = vx - worldX * zNew;
        const newPanY = vy - worldY * zNew;

        // ⚡️write both instantly, same frame
        zoomRef.current = zNew;
        setZoomVar(zNew);
        setPanInstant(newPanX, newPanY);
    }

    function setZoomVar(z) {
        if (scaledLayerRef.current) {
            scaledLayerRef.current.style.setProperty("--bt-zoom", z);
        }
    }
    const drag = useRef({
        active: false,
        startX: 0, startY: 0,
        panStartX: 0, panStartY: 0,
        movedEnough: false,
    });

    function setPanInstant(x, y) {
        if (panLayerRef.current) {
            panLayerRef.current.style.transform = `translate(${x}px, ${y}px)`; // immediate
        }
        setPan({ x, y }); // sync React state (next paint)
    }

    function isOnBackground(target) {
        // if click is inside the scaled layer, refuse to pan
        if (scaledLayerRef.current && scaledLayerRef.current.contains(target)) return false;
        return true;
    }

    function onPointerDown(e) {
        if (e.button !== 0) return;
        if (!isOnBackground(e.target)) return;

        e.currentTarget.setPointerCapture?.(e.pointerId);
        drag.current = {
            active: true,
            captured: true,
            startX: e.clientX,
            startY: e.clientY,
            panStartX: panRef.current.x,
            panStartY: panRef.current.y,
            movedEnough: false,
        };

        // 👇 disable selection right away
        setUserSelect(true);

        // visual feedback
        e.currentTarget.classList.add("cursor-grabbing", "bt-noselect");
    }

    function clientToWorld(clientX, clientY) {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        const localX = clientX - rect.left;
        const localY = clientY - rect.top;

        const scale = zoomRef.current || 1;
        const pan = panRef.current || { x: 0, y: 0 };

        return {
            x: (localX - pan.x) / scale,
            y: (localY - pan.y) / scale,
        };
    }

    function handleDragOver(e) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
    }

    function handleDrop(e) {
        e.preventDefault();
        const raw = e.dataTransfer.getData("application/x-bowtie");
        if (!raw) return;

        const payload = JSON.parse(raw);
        const { x, y } = clientToWorld(e.clientX, e.clientY);

        if (payload.type === "threat") addThreatAt(x, y);
        if (payload.type === "consequence") addConsequenceAt(x, y);
    }

    function addThreatAt(x, y) {
        // optional: snap to grid
        // const gx = Math.round(x / 20) * 20;
        // const gy = Math.round(y / 20) * 20;
        addThreat({ x, y });
    }

    function addConsequenceAt(x, y) {
        addConsequence({ x, y });
    }


    let panRaf = null;
    function queuePan(x, y) {
        if (panRaf) return;
        panRaf = requestAnimationFrame(() => {
            panRaf = null;
            setPan({ x, y });
        });
    }

    function onPointerDown(e) {
        // left button only (change if you want middle too)
        if (e.button !== 0) return;

        // if the target (or any ancestor) has data-nopan, don't start a background pan
        if (e.target.closest("[data-nopan]")) return;

        // capture moves outside the element
        e.currentTarget.setPointerCapture?.(e.pointerId);

        drag.current = {
            active: true,
            startX: e.clientX,
            startY: e.clientY,
            panStartX: panRef.current.x,
            panStartY: panRef.current.y,
            movedEnough: false,
        };

        setGlobalUserSelect(true);

        // UX
        e.currentTarget.classList.add("cursor-grabbing");
    }

    function onPointerMove(e) {
        if (!drag.current.active) return;

        const dx = e.clientX - drag.current.startX;
        const dy = e.clientY - drag.current.startY;

        queuePan(drag.current.panStartX + dx, drag.current.panStartY + dy);
    }


    function onPointerUp(e) {
        if (drag.current.captured) {
            e.currentTarget.releasePointerCapture?.(e.pointerId);
        }
        drag.current = {
            active: false,
            captured: false,
            startX: 0, startY: 0,
            panStartX: 0, panStartY: 0,
            movedEnough: false,
        };

        // 👇 re-enable text selection
        setGlobalUserSelect(true);

        e.currentTarget.classList.remove("cursor-grabbing", "bt-noselect");
    }

    const dragState = useRef({ dragging: false, startX: 0, startY: 0, panStartX: 0, panStartY: 0 });

    const zoomRef = useRef(1);
    const clamp = (z) => Math.max(0.5, Math.min(2, z));  // 50%..200%

    function applyZoom(z) {
        const r = getRect();
        applyZoomAt(z, { x: r.width / 2, y: r.height / 2 });
    }

    const zoomIn = () => applyZoom(zoomRef.current * 1.1);
    const zoomOut = () => applyZoom(zoomRef.current / 1.1);
    const zoomReset = () => {
        zoomRef.current = 1;
        setZoomVar(1);
        setPanInstant(0, 0);
    };


    const BASE_GAP = 140;   // minimum px from hazard center
    const PER_CTRL = 36;    // extra px per control on that line
    const MAX_CTRL_BONUS = 8;


    function setUserSelect(disable) {
        const el = canvasRef.current;
        if (!el) return;
        el.style.userSelect = disable ? "none" : "";
        el.style.webkitUserSelect = disable ? "none" : "";
    }


    function setGlobalUserSelect(disable) {
        const v = disable ? "none" : "";
        // lock both canvas and the whole page (prevents selection even if you cross over nodes/inputs)
        if (canvasRef.current) {
            canvasRef.current.style.userSelect = v;
            canvasRef.current.style.webkitUserSelect = v;
        }
        document.body.style.userSelect = v;
        document.body.style.webkitUserSelect = v;
    }


    function onPanMove(e) {
        const ds = dragState.current;
        if (!ds.dragging) return;

        const dx = e.clientX - ds.startX;
        const dy = e.clientY - ds.startY;

        setPan({ x: ds.panStartX + dx, y: ds.panStartY + dy });
    }

    function endPanDrag() {
        dragState.current.dragging = false;
        window.removeEventListener("mousemove", onPanMove);
    }

    let zoomRaf = null;
    let pending = null; // { z, anchor:{x,y} }

    function flushZoom() {
        if (!pending) return;
        const { z, anchor } = pending; pending = null;

        const rect = getRect();
        const oldZ = zoomRef.current;
        const zNew = clamp(Number(z.toFixed(3)));

        const vx = anchor?.x ?? rect.width / 2;
        const vy = anchor?.y ?? rect.height / 2;

        // world coords under cursor BEFORE zoom
        const worldX = (vx - panRef.current.x) / oldZ;
        const worldY = (vy - panRef.current.y) / oldZ;

        // pan so same world point stays under cursor AFTER zoom
        const newPanX = vx - worldX * zNew;
        const newPanY = vy - worldY * zNew;

        zoomRef.current = zNew;
        setZoomVar(zNew);
        setPan({ x: newPanX, y: newPanY });
    }


    function normalizeWheelDelta(e) {
        if (e.deltaMode === 1) return e.deltaY * 16;
        if (e.deltaMode === 2) return e.deltaY * 800;
        return e.deltaY;
    }
    function handleWheelNative(e) {
        e.preventDefault();
        const rect = getRect();
        const anchor = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        const dy = normalizeWheelDelta(e);
        const factor = Math.exp(-dy / 600);  // smooth
        const target = zoomRef.current * factor;
        applyZoomAt(target, anchor);
    }

    useEffect(() => {
        const el = canvasRef.current;
        if (!el) return;
        el.addEventListener("wheel", handleWheelNative, { passive: false });
        return () => el.removeEventListener("wheel", handleWheelNative, { passive: false });
    }, [canvasRef]);


    const toPct = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return null;
        return Math.max(0, Math.min(100, n));
    };

    useEffect(() => {
        const threatControls = bowtie?.threatControls ?? bowtie?.threatcontrols ?? null;
        if (!threatControls) return;

        const threats = formState.BowtieThreats || [];
        if (!threats.length || !threats.some(t => t.ThreatID != null)) return;

        const idToClient = new Map(
            threats.filter(t => t.ThreatID != null).map(t => [t.ThreatID, t.ClientID])
        );

        const grouped = {};
        for (const row of threatControls) {
            const ownerThreatId = row.ThreatID ?? row.threat_id;
            const ownerKey = idToClient.get(ownerThreatId);
            if (!ownerKey) continue;

            const dbControlId = row.control_id ?? row.ThreatControlID ?? null;

            (grouped[ownerKey] ||= []).push({
                ClientID: `dbtc-${ownerThreatId}-${row.ThreatControlID}-${row.OrderIndex ?? 0}`,
                ThreatControlID: row.ThreatControlID ?? null,
                Text: row.Text || "",
                OrderIndex: row.OrderIndex ?? 0,
                DbControlID: dbControlId,

                // NEW: normalized efficiency (accepts EfficiencyLevel or efficiency_level)
                efficiency: toPct(row.EfficiencyLevel ?? row.efficiency_level),
            });
        }

        Object.values(grouped).forEach(list =>
            list.sort((a, b) => (a.OrderIndex || 0) - (b.OrderIndex || 0))
        );

        setFormState(s => ({
            ...s,
            ThreatCtrlByOwner: { ...(s.ThreatCtrlByOwner || {}), ...grouped },
        }));
    }, [bowtie?.threatControls, formState.BowtieThreats]);
    // hydrate Consequence controls


    useEffect(() => {
        if (!bowtie?.consequenceControls) return;

        const cons = formState.BowtieConsequences || [];
        if (!cons.length || !cons.some(c => c.ConsequenceID != null)) return;

        const idToClient = new Map(
            cons.filter(c => c.ConsequenceID != null).map(c => [c.ConsequenceID, c.ClientID])
        );

        const grouped = {};
        for (const row of bowtie.consequenceControls) {
            const ownerConsId = row.ConsequenceID ?? row.consequence_id;
            const dbControlId = row.ConsequenceControlID ?? row.control_id ?? null;
            const ownerKey = idToClient.get(ownerConsId);
            if (!ownerKey) continue;

            (grouped[ownerKey] ||= []).push({
                ClientID: `dbcc-${ownerConsId}-${dbControlId}-${row.OrderIndex ?? 0}`,
                ConsequenceControlID: row.ConsequenceControlID ?? null,
                Text: row.Text || "",
                OrderIndex: row.OrderIndex ?? 0,
                DbControlID: dbControlId,

                // NEW: normalized efficiency (accepts EfficiencyLevel or efficiency_level)
                efficiency: toPct(row.EfficiencyLevel ?? row.efficiency_level),
            });
        }

        Object.values(grouped).forEach(list =>
            list.sort((a, b) => (a.OrderIndex || 0) - (b.OrderIndex || 0))
        );

        setFormState(s => ({
            ...s,
            ConsequenceCtrlByOwner: { ...(s.ConsequenceCtrlByOwner || {}), ...grouped },
        }));
    }, [bowtie?.consequenceControls, formState.BowtieConsequences]);


    useEffect(() => {
        if (bowtie?.threats) {
            setFormState(s => ({
                ...s,
                BowtieThreats: bowtie.threats.map(t => ({
                    ClientID: `dbc-${t.ThreatID}`,     // stable key
                    ThreatID: t.ThreatID,             // DB id (may be null for new)
                    Text: t.Text || "",
                    OrderIndex: t.OrderIndex ?? 0,
                })),
            }));
        }
    }, [bowtie?.threats]);

    const initializedRef = useRef(false);

    useEffect(() => {
        // bowtie shape: { bowtie, threats, threatControls, ... }
        if (!bowtie?.bowtie?.bowtie_id) return;     // wait until real data
        if (initializedRef.current) return;         // don’t overwrite edits after first load

        const next = normalizeBowtieToForm(bowtie);
        setFormState(prev => ({ ...prev, ...next }));
        initializedRef.current = true;
    }, [bowtie?.bowtie?.bowtie_id]);

    useEffect(() => {
        if (bowtie?.consequences) {
            setFormState(s => ({
                ...s,
                BowtieConsequences: bowtie.consequences.map((c, i) => ({
                    ClientID: `dbc-${c.ConsequenceID}`,   // stable client key for DB rows
                    ConsequenceID: c.ConsequenceID,       // DB id (null for new)
                    Text: c.Text || "",
                    OrderIndex: c.OrderIndex ?? i,
                })),
            }));
        }
    }, [bowtie?.consequences]);

    useEffect(() => {
        const fetchBowtie = async () => {
            try {
                // try getting existing bowtie
                await getBowtieFN(risk_id);
            } catch (err) {
                // if backend returns 404, create one
                if (err.response && err.response.status === 404) {
                    await createBowtieFN(risk_id);
                } else {
                    console.error("Bowtie fetch error:", err);
                }
            }
        };

        if (risk_id) fetchBowtie();
    }, [risk_id, getBowtieFN, createBowtieFN]);


    const history = useHistory();

    const handleSaveAndExit = async () => {

        try {
            console.log(bowtie)
            console.log(formState)
            await saveBowtieFN(bowtie?.bowtie.risk_id, bowtie.bowtie, formState);
            // toast/snackbar is nicer than alert; keeping it simple:
            console.log(activeDepartmentId)
            history.push('/units', {
                activeMenuItem: 'risk',
                departmentfromredirect: activeDepartmentId
            });
        } catch (e) {
            //alert("Failed to save. Check console.");
        }

    };

    const makeTempId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const makeClientId = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    function addThreatControl(
        ownerThreatClientID,
        text,
        dbControlId = null,
        controlType = "Preventive",
        efficiency = null // ← NEW
    ) {
        const normalizeDbId = (v) => (Number.isInteger(v) && v > 0 ? v : 0);

        setFormState((prev) => {
            const list = prev.ThreatCtrlByOwner?.[ownerThreatClientID] || [];
            const nextCtrl = {
                ClientID: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                Text: text,
                DbControlID: normalizeDbId(dbControlId), // 0 if manual
                Type: controlType,
                ThreatControlID: 0,                      // ALWAYS 0 for new/unsaved
                efficiency: toPct(efficiency),           // ← NEW (0–100 or null)
            };
            return {
                ...prev,
                ThreatCtrlByOwner: {
                    ...(prev.ThreatCtrlByOwner || {}),
                    [ownerThreatClientID]: [...list, nextCtrl],
                },
            };
        });
    }

    const updateThreatControlText = (ownerKey, controlClientId, value) => {
        setFormState(s => {
            const list = s.ThreatCtrlByOwner[ownerKey] || [];
            const next = list.map(c => c.ClientID === controlClientId ? { ...c, Text: value } : c);
            return { ...s, ThreatCtrlByOwner: { ...s.ThreatCtrlByOwner, [ownerKey]: next } };
        });
    };

    const removeThreatControl = (ownerKey, controlClientId) => {
        console.log("trying ot rmeove")
        setFormState(s => {
            const list = s.ThreatCtrlByOwner[ownerKey] || [];
            const next = list
                .filter(c => c.ClientID !== controlClientId)
                .map((c, i) => ({ ...c, OrderIndex: i }));
            return { ...s, ThreatCtrlByOwner: { ...s.ThreatCtrlByOwner, [ownerKey]: next } };
        });
    };


    const updateThreatText = (clientId, value) => {
        setFormState(s => ({
            ...s,
            BowtieThreats: s.BowtieThreats.map(t =>
                t.ClientID === clientId ? { ...t, Text: value } : t
            ),
        }));
    };

    const addThreat = () => {
        setFormState(s => {
            const clientId = makeTempId();
            return {
                ...s,
                BowtieThreats: [
                    ...s.BowtieThreats,
                    { ClientID: clientId, ThreatID: null, Text: "", OrderIndex: s.BowtieThreats.length },
                ],
                ThreatCtrlByOwner: {
                    ...s.ThreatCtrlByOwner,
                    [clientId]: s.ThreatCtrlByOwner[clientId] || [], // ensure bucket exists
                },
            };
        });
        setShowActions(false);
    };

    const removeThreat = (clientId) => {
        setFormState(s => {
            const nextThreats = s.BowtieThreats
                .filter(t => t.ClientID !== clientId)
                .map((t, i) => ({ ...t, OrderIndex: i }));
            const { [clientId]: _omit, ...rest } = s.ThreatCtrlByOwner;
            return { ...s, BowtieThreats: nextThreats, ThreatCtrlByOwner: rest };
        });
    };

    const allowDrop = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
    };

    const parseDragPayload = (e) => {
        try {
            const raw = e.dataTransfer.getData("application/json");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    };

    const handleDropOnThreat = (e, ownerThreatClientID) => {
        e.preventDefault();
        const data = parseDragPayload(e); // { type, control_id, text, efficiency? }
        if (!data || !data.text) return;

        // Accept Preventive (and alias 'Detective' if older data sneaks in)
        if (data.type && !["Preventive", "Detective"].includes(data.type)) return;

        const eff =
            data.efficiency ??
            data.EfficiencyLevel ??
            data.efficiency_level ??
            null;

        console.log("Dropped id", data);

        addThreatControl(
            ownerThreatClientID,
            data.text.trim(),
            data.control_id ?? null,
            data.type ?? "Preventive",
            eff // ← pass efficiency through
        );
    };

    const handleDropOnConsequence = (e, ownerConsequenceClientID) => {
        e.preventDefault();
        const data = parseDragPayload(e); // { type, control_id, text, efficiency? }
        if (!data || !data.text) return;

        // Only accept Mitigative for consequences
        if (data.type && data.type !== "Mitigative") return;

        const eff = data.efficiency ?? data.EfficiencyLevel ?? data.efficiency_level ?? null;

        addConsequenceControl(
            ownerConsequenceClientID,
            data.text.trim(),
            data.control_id ?? null,
            "Mitigative",
            eff // ← pass it through
        );
    };

    const onThreatControlIconClick = (ctrl, e) => {
        e.stopPropagation();
        const controlId = ctrl.DbControlID ?? ctrl.ThreatControlID ?? 0; // ← fallback
        console.log("Threat control clicked →", controlId);
        if (controlId != 0) {
            setSelectedControl(controlId);
        }
        // later: if (ctrl.DbControlID) openControlPanel(ctrl.DbControlID); else openThreatControlRow(ctrl.ThreatControlID);
    };

    const onConsequenceControlRowClick = (ctrl, e) => {
        e.stopPropagation();
        const controlId = ctrl.DbControlID ?? 0; // prefer DB id if present
        console.log("Consequence control clicked →", controlId);
        // later: if (controlId) openControlPanel(controlId);
        if (controlId != 0) {
            setSelectedControl(controlId);
        }
    };


    function addConsequenceControl(
        ownerConsequenceClientID,
        text,
        dbControlId = null,
        controlType = "Mitigative",
        efficiency = null // ← NEW
    ) {
        const normalizeDbId = (v) => {
            const n = Number(v);
            return Number.isInteger(n) && n > 0 ? n : 0;
        };

        setFormState((prev) => {
            const list = prev.ConsequenceCtrlByOwner?.[ownerConsequenceClientID] || [];
            const nextCtrl = {
                ClientID: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                Text: text,
                DbControlID: normalizeDbId(dbControlId), // 0 if manual
                Type: controlType,
                ConsequenceControlID: 0,                 // ALWAYS 0 for new/unsaved
                efficiency: toPct(efficiency),           // ← NEW (0–100 or null)
            };
            return {
                ...prev,
                ConsequenceCtrlByOwner: {
                    ...(prev.ConsequenceCtrlByOwner || {}),
                    [ownerConsequenceClientID]: [...list, nextCtrl],
                },
            };
        });
    }

    const updateConsequenceControlText = (ownerKey, controlClientId, value) => {
        setFormState(s => {
            const list = s.ConsequenceCtrlByOwner[ownerKey] || [];
            const next = list.map(c => c.ClientID === controlClientId ? { ...c, Text: value } : c);
            return { ...s, ConsequenceCtrlByOwner: { ...s.ConsequenceCtrlByOwner, [ownerKey]: next } };
        });
    };

    const removeConsequenceControl = (ownerKey, controlClientId) => {
        console.log("trying ot rmeove")
        setFormState(s => {
            const list = s.ConsequenceCtrlByOwner[ownerKey] || [];
            const next = list
                .filter(c => c.ClientID !== controlClientId)
                .map((c, i) => ({ ...c, OrderIndex: i }));
            return { ...s, ConsequenceCtrlByOwner: { ...s.ConsequenceCtrlByOwner, [ownerKey]: next } };
        });
    };

    const updateConsequenceText = (clientId, value) => {
        setFormState(s => ({
            ...s,
            BowtieConsequences: s.BowtieConsequences.map(c =>
                c.ClientID === clientId ? { ...c, Text: value } : c
            ),
        }));
    };

    const addConsequence = () => {
        setFormState(s => {
            const clientId = `tmpc-${makeTempId()}`;
            return {
                ...s,
                BowtieConsequences: [
                    ...s.BowtieConsequences,
                    { ClientID: clientId, ConsequenceID: null, Text: "", OrderIndex: s.BowtieConsequences.length },
                ],
                ConsequenceCtrlByOwner: {
                    ...s.ConsequenceCtrlByOwner,
                    [clientId]: s.ConsequenceCtrlByOwner[clientId] || [], // ensure empty bucket
                },
            };
        });
        setShowActions(false);
    };

    const removeConsequence = (clientId) => {
        setFormState(s => {
            const nextCons = s.BowtieConsequences
                .filter(c => c.ClientID !== clientId)
                .map((c, i) => ({ ...c, OrderIndex: i }));
            const { [clientId]: _omit, ...rest } = s.ConsequenceCtrlByOwner;
            return { ...s, BowtieConsequences: nextCons, ConsequenceCtrlByOwner: rest };
        });
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'Extreme':
                return 'text-red-700 font-semibold';
            case 'High':
                return 'text-orange-600 font-semibold';
            case 'Moderate':
                return 'text-yellow-600 font-medium';
            case 'Low':
                return 'text-green-600 font-medium';
            default:
                return '';
        }
    };

    useEffect(() => {
            if (go_to_risk_dashboard) {
                history.push({ pathname: `/dashboards/${go_to_risk_dashboard}` });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [go_to_risk_dashboard]);

    return (
        <div className="flex flex-col w-full h-screen overflow-hidden">
            <TopBar className="fixed top-0 left-0 right-0 z-10" />

            <div className="flex flex-row w-full h-full">
                <Sidebar className="fixed left-0 h-screen z-0" />

                {/* <UnitsSideBar className="fixed left-0 h-screen z-10 w-[250px] min-w-[250px] max-w-[250px]"
                    setActiveDepartment={setActiveDepartmentId}
                    activeDepartmentId={activeDepartmentId}
                    departments={departments}
                /> */}

                <div className="flex flex-col w-full">
                    {/* <UnitsTopBar className="w-full h-16 fixed z-10"
                        activeDepartmentId={activeDepartmentId}
                        departments={departments}
                    /> */}

                    <div className="flex flex-col p-4 min-h-screen mt-6 sm:mt-4">
                        <div className="flex items-center gap-6 py-2">
                            {/* Save & Exit */}
                            <button
                                onClick={handleSaveAndExit}
                                className="flex items-center justify-center gap-2 h-10 px-5 rounded-[5px] border border-gray-300 bg-[#F8F8F8] text-black shadow-md hover:bg-[#F0F0F2] hover:shadow-lg transition"
                            >
                                <LuLogOut className="w-4 h-4 text-gray-700" />
                                <span className="text-sm font-medium">Exit</span>
                            </button>

                            {/* Stats */}
                            {bowtie?.bowtie?.risk_id && (
                                <button
                                    onClick={() => dispatch(gotoRiskDashboard(bowtie.bowtie.risk_id))}
                                    className="flex items-center justify-center gap-2 h-10 px-5 rounded-[5px] border border-gray-300 bg-[#F8F8F8] shadow-md hover:bg-[#F0F0F2] hover:shadow-lg transition"
                                >
                                    <LuActivity
                                        className={`w-4 h-4 ${bowtie?.riskSeverity
                                                ? getSeverityColor(bowtie.riskSeverity).split(' ')[0]
                                                : 'text-gray-500'
                                            }`}
                                    />
                                    <span className="text-sm text-gray-700 font-semibold">Stats</span>
                                </button>
                            )}
                        </div>

                        {/* Canvas row */}
                        <div className="relative flex w-full h-full">


                            <div className="absolute top-4 left-4 z-50 flex flex-col items-center space-y-3">
                                <div className="flex space-x-3">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowActions((p) => !p);
                                        }}
                                        className="w-12 h-12 border rounded-lg shadow-md flex items-center justify-center bg-white hover:bg-gray-50 transition-all"
                                        title="Add Threat / Consequence"
                                    >
                                        <RiAddLine size={20} />
                                    </button>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenPanel((p) => !p);
                                        }}
                                        className="w-12 h-12 border rounded-lg shadow-md flex items-center justify-center bg-white hover:bg-gray-50 transition-all"
                                        title="Toggle available controls"
                                    >
                                        <RiFlowChart size={20} />
                                    </button>
                                </div>

                                {showActions && (
                                    <div className="flex space-x-3 mt-2">
                                        {/* Drag Threat */}
                                        <div className="relative group">
                                            <div
                                                draggable
                                                onDragStart={(e) => beginPaletteDrag(e, "threat")}
                                                onDragEnd={endPaletteDrag}
                                                className="w-12 h-12 rounded-lg border shadow bg-white hover:bg-gray-50 flex items-center justify-center cursor-grab active:cursor-grabbing transition-all"
                                                title="Drag to create Threat"
                                            >
                                                <RiAlertLine size={18} />
                                            </div>
                                            <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                                Drag Threat
                                            </span>
                                        </div>

                                        {/* Drag Consequence */}
                                        <div className="relative group">
                                            <div
                                                draggable
                                                onDragStart={(e) => beginPaletteDrag(e, "consequence")}
                                                onDragEnd={endPaletteDrag}
                                                className="w-12 h-12 rounded-lg border shadow bg-white hover:bg-gray-50 flex items-center justify-center cursor-grab active:cursor-grabbing transition-all"
                                                title="Drag to create Consequence"
                                            >
                                                <RiErrorWarningLine size={18} />
                                            </div>
                                            <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                                Drag Consequence
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Grid canvas */}
                            <div
                                ref={canvasRef}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onPointerDown={onPointerDown}
                                onPointerMove={onPointerMove}
                                onPointerUp={onPointerUp}
                                className="flex-grow h-[calc(100vh-200px)] border border-gray-300 rounded-lg relative overflow-hidden"
                            >
                                {/* subtle grid background */}
                                <div
                                    className="absolute inset-0 z-0 pointer-events-none"
                                    style={{
                                        backgroundColor: "#ffffff",
                                        backgroundImage: "radial-gradient(#e5e7eb 1px, transparent 1px)",
                                        backgroundSize: "18px 18px",       // spacing between dots
                                        backgroundPosition: "0 0",
                                        backgroundAttachment: "local",
                                    }}
                                />

                                <div
                                    ref={panLayerRef}
                                    className="absolute inset-0 z-5 will-change-transform"
                                    style={{ transform: `translate(${pan.x}px, ${pan.y}px)`, transition: "none" }}
                                >

                                    <div
                                        ref={scaledLayerRef}
                                        className="absolute inset-0 z-10 origin-top-left"
                                        style={{ transform: "scale(var(--bt-zoom, 1))", transition: "none" }}
                                    >



                                        <svg
                                            className="absolute inset-0 pointer-events-none z-[5]"
                                            width={3000}
                                            height={2000}
                                            viewBox="0 0 3000 2000"
                                            preserveAspectRatio="none"
                                        >
                                            {threatLines.map((ln) => {
                                                const active = hoveredThreat === ln.id;
                                                const stroke = active ? "rgba(239,68,68,0.95)" : "rgba(239,68,68,0.7)";
                                                const width = active ? 4 : 3;
                                                return (
                                                    <path
                                                        key={ln.id}
                                                        d={makeHorizSmootherConnector(ln)}                  // <— use the bi-directional helper
                                                        fill="none"
                                                        stroke={active ? "rgba(0,0,0,0.9)" : "rgba(0,0,0,0.6)"}      // near-black with a bit of transparency
                                                        strokeWidth={active ? 2 : 1.5}
                                                        vectorEffect="non-scaling-stroke"    // <— keeps width constant while zooming
                                                        shapeRendering="geometricPrecision"
                                                        strokeLinecap="round"
                                                    />
                                                );
                                            }

                                            )}
                                        </svg>

                                        {threatLines.map((ln) => {
                                            const controls = formState.ThreatCtrlByOwner?.[ln.ownerId] || [];

                                            const anchors = getHorizAnchorsFixedGap(ln, controls.length, {
                                                bend: 110,
                                                startPad: 8,       // close to the Threat card
                                                gap: 170,          // keep big spacing after first
                                                firstFactor: 0.35, // ← ~60% closer than 0.5; try 0.3 if you want even closer
                                                endPad: 4,
                                                fanStep: 8,
                                            });

                                            const HANG = 36;

                                            return anchors.map((p, i) => {
                                                const ctrl = controls[i];
                                                //console.log("Contorl", ctrl)
                                                if (!ctrl) return null;

                                                return (
                                                    <div
                                                        key={ctrl.ClientID}
                                                        data-nopan
                                                        className="absolute z-30 flex flex-col items-center pointer-events-auto"
                                                        style={{ left: p.x, top: ln.y1 }}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                    >
                                                        {/* tiny square also clickable */}
                                                        <div
                                                            className="w-4 h-4 border-2 border-gray-700 rounded-md bg-white shadow-sm relative z-[6] cursor-pointer"
                                                            style={{ transform: "translateY(-5px)" }}
                                                            onClick={(e) => onThreatControlIconClick(ctrl, e)}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") onThreatControlIconClick(ctrl, e);
                                                            }}
                                                        />

                                                        {/* make the whole card clickable */}
                                                        <div
                                                            className="mt-2 w-[150px] rounded-md border bg-white shadow-sm overflow-hidden cursor-pointer"
                                                            style={{ marginTop: HANG - 24 }}
                                                            onClick={(e) => onThreatControlIconClick(ctrl, e)}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") onThreatControlIconClick(ctrl, e);
                                                            }}
                                                        >
                                                            <div className="flex items-center justify-between px-2 py-1 bg-gray-100 border-b select-none">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="text-[10px] font-semibold text-gray-800">
                                                                        Preventive control
                                                                    </span>

                                                                    {/* efficiency dot */}
                                                                    {ctrl.efficiency != null && (
                                                                        <span
                                                                            className="inline-block w-2.5 h-2.5 rounded-full"
                                                                            title={`Efficiency: ${ctrl.efficiency}%`}
                                                                            style={{
                                                                                backgroundColor:
                                                                                    ctrl.efficiency >= 75
                                                                                        ? "#00AFB9" // teal = high
                                                                                        : ctrl.efficiency >= 50
                                                                                            ? "#FFD166" // yellow = medium
                                                                                            : "#EF476F", // red = low
                                                                            }}
                                                                        />
                                                                    )}
                                                                </div>

                                                                <button
                                                                    type="button"
                                                                    className="text-gray-600 hover:text-red-600 px-1 py-0.5 rounded hover:bg-gray-200"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        removeThreatControl(ln.ownerId, ctrl.ClientID);
                                                                    }}
                                                                    aria-label="Remove control"
                                                                    title="Remove control"
                                                                >
                                                                    <FiTrash2 size={11} />
                                                                </button>
                                                            </div>
                                                            <div className="p-1.5">
                                                                <textarea
                                                                    value={ctrl.Text || ""}
                                                                    onChange={(e) => updateThreatControlText(ln.ownerId, ctrl.ClientID, e.target.value)}
                                                                    placeholder="Control description"
                                                                    className="w-full bg-transparent outline-none border-none focus:ring-0 text-[12px] text-gray-900 font-normal leading-snug resize-none overflow-hidden"
                                                                    rows={2}
                                                                    draggable={false}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    onFocus={(e) => e.stopPropagation()}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })}
                                        {/* Amber connectors: Hazard → Consequences */}
                                        <svg
                                            className="absolute inset-0 pointer-events-none"
                                            width={svgW}
                                            height={svgH}
                                            viewBox={`0 0 ${svgW} ${svgH}`}
                                            preserveAspectRatio="none"
                                        >
                                            {threatLines.map((ln) => {
                                                const active = hoveredThreat === ln.id;
                                                return (
                                                    <path
                                                        key={ln.id}
                                                        d={makeHorizSmootherConnector(ln)}           // world coords
                                                        fill="none"
                                                        stroke={active ? "rgba(0,0,0,0.9)" : "rgba(0,0,0,0.6)"}      // near-black with a bit of transparency
                                                        strokeWidth={active ? 2 : 1.5}
                                                        vectorEffect="non-scaling-stroke"  // keeps stroke width constant while zooming
                                                        shapeRendering="geometricPrecision"
                                                        strokeLinecap="round"
                                                    />
                                                );
                                            })}
                                            {consequenceLines.map((ln) => {
                                                const active = hoveredConsequence === ln.id;
                                                return (
                                                    <path
                                                        key={ln.id}
                                                        d={makeHorizSmootherConnector(ln)}
                                                        fill="none"
                                                        stroke={active ? "rgba(0,0,0,0.9)" : "rgba(0,0,0,0.6)"}      // near-black with a bit of transparency
                                                        strokeWidth={active ? 2 : 1.5}
                                                        vectorEffect="non-scaling-stroke"
                                                        shapeRendering="geometricPrecision"
                                                        strokeLinecap="round"
                                                    />
                                                );
                                            })}
                                        </svg>

                                        {consequenceLines.map((ln) => {
                                            const controls = formState.ConsequenceCtrlByOwner?.[ln.ownerId] || [];

                                            const anchors = getHorizAnchorsFixedGapSide(ln, controls.length, {
                                                bend: 110,
                                                startPad: 32,
                                                endPad: 0,        // min clearance from the consequence card
                                                gap: 180,          // your big spacing
                                                firstFactor: 0.35, // make the very first one even closer than half-gap
                                                fanStep: 8,
                                                side: "end",       // 🔥 key change: lay out from the CARD side
                                            });

                                            const HANG = 36;
                                            return anchors.map((p, i) => {
                                                const ctrl = controls[i];
                                                if (!ctrl) return null;

                                                return (
                                                    <div
                                                        key={ctrl.ClientID || `${ln.id}-cc-${i}`}
                                                        data-nopan
                                                        className="absolute z-30 flex flex-col items-center pointer-events-auto"
                                                        style={{ left: p.x, top: ln.y1 }}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <div
                                                            className="w-4 h-4 border-2 border-gray-700 rounded-md bg-white shadow-sm relative z-[6] cursor-pointer"
                                                            style={{ transform: "translateY(-5px)" }}
                                                            onClick={(e) => onConsequenceControlRowClick(ctrl, e)}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") onConsequenceControlRowClick(ctrl, e);
                                                            }}
                                                        />
                                                        <div
                                                            className="mt-2 w-[150px] rounded-md border bg-white shadow-sm overflow-hidden cursor-pointer"
                                                            style={{ marginTop: HANG - 24 }}
                                                            onClick={(e) => onConsequenceControlRowClick(ctrl, e)}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") onConsequenceControlRowClick(ctrl, e);
                                                            }}
                                                        >
                                                            <div className="flex items-center justify-between px-2 py-1 bg-gray-100 border-b select-none">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="text-[10px] font-semibold text-gray-800">Mitigative Control</span>
                                                                    {ctrl.efficiency != null && (
                                                                        <span
                                                                            className="inline-block w-2.5 h-2.5 rounded-full"
                                                                            title={`Efficiency: ${ctrl.efficiency}%`}
                                                                            style={{
                                                                                backgroundColor:
                                                                                    ctrl.efficiency >= 75
                                                                                        ? "#00AFB9" // high
                                                                                        : ctrl.efficiency >= 50
                                                                                            ? "#FFD166" // medium
                                                                                            : "#EF476F", // low
                                                                            }}
                                                                        />
                                                                    )}
                                                                </div>

                                                                <button
                                                                    type="button"
                                                                    className="text-gray-600 hover:text-red-600 px-1 py-0.5 rounded hover:bg-gray-200"
                                                                    onClick={(e) => { e.stopPropagation(); removeConsequenceControl(ln.ownerId, ctrl.ClientID); }}
                                                                    aria-label="Remove control"
                                                                    title="Remove control"
                                                                >
                                                                    <FiTrash2 size={11} />
                                                                </button>
                                                            </div>

                                                            <div className="p-1.5">
                                                                <textarea
                                                                    value={ctrl.Text || ""}
                                                                    onChange={(e) => updateConsequenceControlText(ln.ownerId, ctrl.ClientID, e.target.value)}
                                                                    placeholder="Control description"
                                                                    className="w-full bg-transparent outline-none border-none focus:ring-0 text-[12px] text-gray-900 font-normal leading-snug resize-none overflow-hidden"
                                                                    rows={2}
                                                                    draggable={false}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    onFocus={(e) => e.stopPropagation()}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })}

                                        {/* LEFT COLUMN: Threat cards */}
                                        <div className="absolute inset-0">
                                            {(formState.BowtieThreats || []).map((t, idx) => {
                                                const count = (formState.ThreatCtrlByOwner?.[t.ClientID] || []).length;
                                                const startX = 24 - Math.min(count * 50, 500);
                                                const startY = 96 + idx * 150;

                                                return (
                                                    <DraggableThreatCard
                                                        key={
                                                            touchedThreatRef.current.has(t.ClientID)
                                                                ? t.ClientID
                                                                : `${t.ClientID}:${startX},${startY}` // 👈 remount until user drags
                                                        }
                                                        threat={t}
                                                        ownerId={t.ClientID}
                                                        threatControls={(formState.ThreatCtrlByOwner?.[t.ClientID] || [])}
                                                        onRemoveThreat={removeThreat}
                                                        onUpdateThreatText={updateThreatText}
                                                        onAddThreatControl={addThreatControl}
                                                        onRemoveThreatControl={removeThreatControl}
                                                        onUpdateThreatControlText={updateThreatControlText}
                                                        onThreatControlIconClick={onThreatControlIconClick}
                                                        allowDrop={allowDrop}
                                                        handleDropOnThreat={handleDropOnThreat}
                                                        onHoverChange={setHoveredThreat}
                                                        initialPos={{ x: startX, y: startY }}
                                                        onDragStart={() => {
                                                            touchedThreatRef.current.add(t.ClientID);
                                                            draggingThreatRef.current = {
                                                                id: t.ClientID,
                                                                pt: threatAnchorsRef.current[t.ClientID] || null,
                                                            };
                                                        }}
                                                        onDragEnd={() => {
                                                            draggingThreatRef.current = { id: null, pt: null };
                                                            touchedThreatRef.current.add(t.ClientID); // 👈 stop future remounts
                                                        }}
                                                        reportAnchor={reportThreatAnchor}
                                                        canvasRef={scaledLayerRef}
                                                        zoomRef={zoomRef}
                                                    />
                                                );
                                            })}
                                        </div>


                                        {/* CENTER: Hazard node (now circular, custom color) */}
                                        <div
                                            ref={hazardRef}
                                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
                                        >
                                            <div
                                                className="flex items-center justify-center text-white rounded-full shadow-lg font-bold text-lg text-center"
                                                style={{
                                                    width: 150,
                                                    height: 150,
                                                    padding: 0,
                                                    lineHeight: "1.2",
                                                    backgroundColor: "#000000", // ← new color
                                                }}
                                            >
                                                {bowtie?.bowtie?.risk_title || "Hazard"}
                                            </div>
                                        </div>

                                        {/* RIGHT COLUMN: Consequence cards */}
                                        <div className="absolute inset-0">
                                            {(formState.BowtieConsequences || []).map((c, idx) => {
                                                const count = (formState.ConsequenceCtrlByOwner?.[c.ClientID] || []).length;
                                                const startX = getRightStartC(canvasRef) + Math.min(count * 80, 600);
                                                const startY = 96 + idx * 150;

                                                return (
                                                    <DraggableConsequenceCard
                                                        key={
                                                            touchedConsRef.current.has(c.ClientID)
                                                                ? c.ClientID
                                                                : `${c.ClientID}:${startX},${startY}` // 👈 remount when computed start changes
                                                        }
                                                        consequence={c}
                                                        ownerId={c.ClientID}
                                                        consequenceControls={(formState.ConsequenceCtrlByOwner?.[c.ClientID] || [])}

                                                        removeConsequence={removeConsequence}
                                                        updateConsequenceText={updateConsequenceText}
                                                        addConsequenceControl={addConsequenceControl}
                                                        removeConsequenceControl={removeConsequenceControl}
                                                        updateConsequenceControlText={updateConsequenceControlText}
                                                        onConsequenceControlRowClick={onConsequenceControlRowClick}

                                                        allowDrop={allowDrop}
                                                        handleDropOnConsequence={handleDropOnConsequence}
                                                        onHoverChange={setHoveredConsequence}

                                                        initialPos={{ x: startX, y: startY }}

                                                        onDragStart={() => {
                                                            touchedConsRef.current.add(c.ClientID);
                                                            draggingConsRef.current = { id: c.ClientID, pt: consAnchorsRef.current[c.ClientID] || null };
                                                        }}
                                                        onDragEnd={() => {
                                                            draggingConsRef.current = { id: null, pt: null };
                                                            touchedConsRef.current.add(c.ClientID);        // 👈 mark as user-touched, stop remounts
                                                        }}

                                                        reportAnchor={reportConsequenceAnchor}
                                                        canvasRef={scaledLayerRef}
                                                        zoomRef={zoomRef}
                                                    />
                                                );
                                            })}
                                        </div>

                                    </div>
                                </div>
                                {/* /Grid canvas */}
                                <div
                                    className="absolute bottom-4 left-4 z-20"
                                    data-nopan
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onWheel={(e) => e.stopPropagation()}
                                >
                                    <div className="flex items-start gap-2">
                                        {/* ZOOM STACK */}
                                        <div className="flex flex-col items-center overflow-hidden bg-white/85 backdrop-blur border rounded-md shadow">
                                            <button onClick={zoomIn} type="button" title="Zoom in" className="w-8 h-8 flex items-center justify-center hover:bg-gray-50 active:scale-95" aria-label="Zoom in">
                                                <LuZoomIn size={16} />
                                            </button>
                                            <div className="w-full h-px bg-gray-200" />
                                            <button onClick={zoomOut} type="button" title="Zoom out" className="w-8 h-8 flex items-center justify-center hover:bg-gray-50 active:scale-95" aria-label="Zoom out">
                                                <LuZoomOut size={16} />
                                            </button>
                                            <div className="w-full h-px bg-gray-200" />
                                            <button onClick={zoomReset} type="button" title="Reset zoom" className="w-8 h-8 flex items-center justify-center hover:bg-gray-50 active:scale-95" aria-label="Reset zoom">
                                                <LuRotateCcw size={16} />
                                            </button>
                                        </div>

                                        {/* 📊 STATS PILL */}
                                        <div className="flex flex-col justify-between h-[100px] w-[175px] px-3 py-2 bg-white/85 backdrop-blur border rounded-md shadow">
                                            {/* Risk level */}
                                            <div className="flex items-center justify-between leading-none">
                                                <span className="text-[11px] text-gray-500">Risk Level</span>
                                                <span className={`text-[11px] ${getSeverityColor(bowtie?.riskSeverity)}`}>
                                                    {bowtie?.riskSeverity ?? "—"}
                                                </span>
                                            </div>

                                            {/* Effectiveness */}
                                            <div className="flex flex-col justify-center">
                                                <div className="flex items-center justify-between leading-none">
                                                    <span className="text-[11px] text-gray-500">Effectiveness</span>
                                                    <span className="text-xs text-[#3b3b3b]">
                                                        {bowtie?.controlAvgEfficiency != null ? `${bowtie.controlAvgEfficiency}%` : "—"}
                                                    </span>
                                                </div>
                                                <div className="mt-1 h-1 w-full bg-gray-200 rounded">
                                                    <div
                                                        className="h-1 rounded bg-emerald-500"
                                                        style={{
                                                            width: `${Math.max(0, Math.min(100, bowtie?.controlAvgEfficiency ?? 0))}%`,
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Total controls */}
                                            <div className="flex items-center justify-between leading-none">
                                                <span className="text-[11px] text-gray-500">Controls</span>
                                                <span className="text-xs text-[#3b3b3b]">{bowtie?.controlTotal ?? 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 🧩 The draggable control panel (shown when open) */}
                                <div
                                    className="absolute bottom-4 left-4 z-[1000]"
                                    data-nopan
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onWheel={(e) => e.stopPropagation()}
                                >
                                    <DraggableControlPanel
                                        detectiveTitles={bowtie?.detectiveControls || bowtie?.detectiveTitles || []}
                                        mitigativeTitles={bowtie?.mitigativeControls || bowtie?.mitigativeTitles || []}
                                        visible={openPanel}
                                        pos={panelPos}
                                        setPos={setPanelPos}
                                        onClose={() => setOpenPanel(false)}
                                    />

                                    {selectedControl && (
                                        <Popup
                                            open={true}
                                            onClose={() => setSelectedControl(null)}
                                            contentStyle={{ overflow: 'auto', maxHeight: '80vh', width: '900px', marginTop: '14vh' }}>
                                            <ControlOverlay key={selectedControl || 'new-control'} closeControl={closeCreation} control_id={selectedControl} risk_id={bowtie?.bowtie.risk_id} />
                                        </Popup>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    console.log("Bowtie Stats", state)
    return {
        departments: state.department.departments,
        activeDepartmentId: state.department.activeDepartmentId,
        bowtie: state.risk.bowtie,
        go_to_risk_dashboard: state.risk.go_to_risk_dashboard,
    }
};

const mapDispatchToProps = (dispatch) => ({
    getDepartmentsFN: () => dispatch(getDepartments()),
    setActiveDepartmentId: (department_id) => dispatch(setActiveDepartment(department_id)),
    createBowtieFN: (risk_id) => dispatch(createBowtie(risk_id)),
    getBowtieFN: (risk_id) => dispatch(getBowtie(risk_id)),
    saveBowtieFN: (risk_id, bowtie, fromstate) => dispatch(saveBowtie(risk_id, bowtie, fromstate)),
    editControlFN: (risk_id, control_id, control) => dispatch(editControl(risk_id, control_id, control)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(BowtieAnalysis);